import { createSelector } from "reselect";
import { memoize, uniqueByKey } from "@ls/js-utils";
import SelectionState from "state/selections/SelectionState";
import { selectSideBySideValue } from "state/selections/selector";
import scope from "./scope";
import SummaryType from "state/selections/Constants/SummaryType";

export const selectFetchingBetaDecodes = (state) =>
  state[scope].fetchingBetaDecodes;
export const selectBetaDecodes = (state) => state[scope].betaDecodes;
export const selectFetchingBetaData = (summaryType) => (state) =>
  state[scope]["fetchingBetaData"][summaryType];
export const selectBetaData = (summaryType) => (state) =>
  state[scope]["betaData"][summaryType];
export const selectFetchingBetaSbsData = (summaryType) => (state) =>
  state[scope]["fetchingBetaSbsData"][summaryType];
export const selectBetaSbsData = (summaryType) => (state) =>
  state[scope]["betaSbsData"][summaryType];

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return (state) => state[scope]["definitions"][selectionState];
  }

  return createSelector(
    (state) => state[scope]["definitions"][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(
        ({ complete }) => complete
      );
      return completeDefinitions.filter(
        uniqueByKey(completeDefinitions, "key")
      );
    }
  );
});

export const selectBetaDecodesSelectionOptions = createSelector(
  selectBetaDecodes,
  (betaDecodes) => {
    if (betaDecodes && betaDecodes.length > 0) {
      return betaDecodes.map(({ betaId, betaName }) => ({
        label: `${betaName} (${betaId})`,
        value: betaId,
      }));
    }
    return [];
  }
);

export const selectRowsMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectBetaData(SummaryType.ABSOLUTE),
  selectBetaData(SummaryType.RELATIVE),
  selectBetaSbsData(SummaryType.ABSOLUTE),
  selectBetaSbsData(SummaryType.RELATIVE),
  selectSideBySideValue(SelectionState.COMMITTED),
  (definitions, absData, relData, absSbsData, relSbsData, sideBySide) => {
    const absEntitySummaries = absData?.root?.entitySummaries ?? [];
    const relEntitySummaries = relData?.root?.entitySummaries ?? [];
    const absSbsEntitySummaries = absSbsData?.root?.entitySummaries ?? [];
    const relSbsEntitySummaries = relSbsData?.root?.entitySummaries ?? [];
    const entityRowsMap = {};
    const benchmarkEntityIdSet = new Set();
    [...absEntitySummaries, ...relEntitySummaries].forEach((entitySummary) => {
      const benchId = entitySummary?.entityResultMetaData?.benchId ?? null;
      if (benchId) {
        benchmarkEntityIdSet.add(benchId);
      }
    });

    definitions.forEach((definition) => {
      const processEntitySummary = (entitySummary, scenario) => {
        const { betaSummaries, entityResultMetaData } = entitySummary;
        const { entityId } = entityResultMetaData;

        betaSummaries.forEach((betaSummary) => {
          const { primaryKey, values } = betaSummary;
          const colId = `${primaryKey.betaId}_${primaryKey.covarType}_${
            definition.betaValue
          }_${definition.summary}${scenario ? ` - scenario` : ""}`;

          if (!entityRowsMap[entityId]) {
            entityRowsMap[entityId] = {};
          }
          entityRowsMap[entityId][colId] =
            values[definition.betaValue.toLowerCase()];
        });
      };
      (definition.summary === SummaryType.ABSOLUTE
        ? absEntitySummaries
        : relEntitySummaries
      ).forEach((entitySummary) => {
        processEntitySummary(entitySummary);
      });
      if (sideBySide) {
        (definition.summary === SummaryType.ABSOLUTE
          ? absSbsEntitySummaries
          : relSbsEntitySummaries
        ).forEach((entitySummary) => {
          processEntitySummary(entitySummary, true);
        });
      }
    });
    return entityRowsMap;

    //   const rowsMap = {};
    //   const populateRowsMap = (betaData, betaDefinition) => {
    //     const {root = {}} = betaData;
    //     const {entitySummaries = []} = root;
    //     entitySummaries.forEach(({betaSummaries = [], entityResultMetaData = {}}) => {
    //       const {entityId} = entityResultMetaData;
    //       if (!rowsMap[entityId]) {
    //         rowsMap[entityId] = {};
    //       }
    //       betaSummaries.forEach(betaSummary => {
    //         const {primaryKey, values} = betaSummary;
    //         rowsMap[entityId][`${primaryKey.betaId},${primaryKey.covarType},${betaDefinition.betaValue},${betaDefinition.summary}`] = values[betaDefinition.betaValue.toLowerCase()]
    //       });
    //     });
    //   };
    //
    //   definitions.forEach(betaDefinition => {
    //     if (betaDefinition.summary === SummaryType.ABSOLUTE) {
    //       populateRowsMap(absoluteBetaData, betaDefinition);
    //     } else {
    //       populateRowsMap(relativeBetaData, betaDefinition);
    //     }
    //   });
    //
    //   return rowsMap;
  }
);
